import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DataTable, type DataTableColumn } from "@h2j-data-team/ui";
import type { QvdAnalysisCoverage } from "@repo/shared";

import { formatBytes } from "@/lib/health-format";
import { tableMessages } from "@/lib/ui-labels";
import { useTableDensity } from "@/lib/use-table-density";
import { noop, STATIC_TABLE_STATE } from "./qvd-list-utils";

// Categorical palette for the by-type breakdown, assigned by descending-bytes
// order. The folded "__other__" bucket always renders neutral gray.
const SEG_COLORS = [
  "#2F6F4F",
  "#3B82F6",
  "#D9A441",
  "#8B5CF6",
  "#0EA5E9",
  "#B4541F",
  "#14B8A6",
  "#E4655B",
];
const OTHER_COLOR = "#8A8F98";

function typeColor(ext: string, index: number): string {
  return ext === "__other__" ? OTHER_COLOR : SEG_COLORS[index % SEG_COLORS.length];
}

type TypeUsage = QvdAnalysisCoverage["footprintByType"][number];

// QvdFootprintByType renders the total-space breakdown by file extension as a
// per-type table (files, size, % of total).
export function QvdFootprintByType({ coverage }: { coverage: QvdAnalysisCoverage }) {
  const { t } = useTranslation("qvd-analysis");
  const { density } = useTableDensity();
  const total = coverage.footprintTotalBytes;
  const types = coverage.footprintByType;

  const columns = useMemo<DataTableColumn<TypeUsage>[]>(() => {
    const label = (ext: string) =>
      ext === "__other__"
        ? t("footprint.otherType")
        : ext === ""
          ? t("footprint.noExtType")
          : ext.toUpperCase();
    const share = (bytes: number) => (total > 0 ? (bytes / total) * 100 : 0);
    return [
      {
        id: "type",
        header: t("footprint.cols.type"),
        rowHeader: true,
        cell: (ty) => (
          <span className="flex items-center gap-2.5">
            <span
              aria-hidden
              className="size-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: typeColor(ty.ext, types.indexOf(ty)) }}
            />
            {label(ty.ext)}
          </span>
        ),
      },
      {
        id: "files",
        header: t("footprint.cols.files"),
        width: 120,
        align: "end",
        variant: "numeric",
        cell: (ty) => <span className="text-dim">{ty.fileCount.toLocaleString()}</span>,
      },
      {
        id: "size",
        header: t("footprint.cols.size"),
        width: 130,
        align: "end",
        variant: "numeric",
        cell: (ty) => formatBytes(ty.bytes),
      },
      {
        id: "pct",
        header: t("footprint.cols.pct"),
        width: 110,
        align: "end",
        variant: "numeric",
        cell: (ty) => <span className="text-dim">{Math.round(share(ty.bytes))}%</span>,
      },
    ];
  }, [t, total, types]);

  if (types.length === 0) return null;

  return (
    <div className="flex flex-col gap-2.5">
      <h2 className="text-xs font-bold uppercase tracking-wider text-faint">
        {t("footprint.byTypeLabel")}
      </h2>
      <DataTable
        label={t("footprint.byTypeLabel")}
        density={density}
        columns={columns}
        rows={types}
        getRowId={(ty) => ty.ext}
        state={STATIC_TABLE_STATE}
        onStateChange={noop}
        messages={tableMessages({ empty: { title: t("files.empty.title") } })}
      />
    </div>
  );
}
